import {
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState
} from "react"
import { v1 as uuid } from "uuid"
import localforage from "localforage"

localforage.config({
    driver: localforage.INDEXEDDB,
    name: "m2mCleaningCleanerFront",
    version: 1.0,
    storeName: "m2m_cleaning_store"
})

const resendableFetchStore = localforage.createInstance({
    name: "BackgroundFetchObject"
})

export type Fetcher = (
    input: RequestInfo,
    init?: RequestInit
) => Promise<Response>

// eslint-disable-next-line no-restricted-globals -- Service Worker経由のバックグラウンドフェッチのfallbackのためfetchを直接使用
export const fetcher = (Fetch?: Fetcher): Fetcher => (Fetch ? Fetch : fetch)

let firstLoad = true

function isUnknownObject(x: unknown): x is Record<PropertyKey, unknown> {
    return x !== null && typeof x !== "undefined" && typeof x === "object"
}

const useBackgroundFetch = () => {
    const [fetchCount, setFetchingCount] = useState(0)
    const inProgress = fetchCount !== 0

    const Refetch = useCallback(
        async (key: string): Promise<Response | never> => {
            setFetchingCount(prev => prev + 1)

            const item = await resendableFetchStore.getItem(key)

            if (typeof item === "string") {
                const data = JSON.parse(item)
                if (isUnknownObject(data) && "info" in data && "init" in data) {
                    const { info, init } = data

                    // eslint-disable-next-line no-restricted-globals -- オフライン時の未送信リクエストを再送するためfetchを直接使用
                    const response = fetch(
                        info as RequestInfo,
                        init as RequestInit | undefined
                    ) // if throw error, promise is rejected.

                    // TODO: Error handling
                    await resendableFetchStore.removeItem(key)
                    setFetchingCount(prev => prev - 1)
                    return response
                }
            }
            throw new Error("invalid")
        },
        []
    )

    const CachingFetch = useCallback(
        async (info: RequestInfo, init?: RequestInit) => {
            setFetchingCount(prev => prev + 1)
            const key = uuid()
            await resendableFetchStore.setItem(
                key,
                JSON.stringify({
                    timestamp: Date.now(), //今の所特に使用しない
                    info,
                    init
                })
            )

            // eslint-disable-next-line no-restricted-globals -- オフライン対応のキャッシュ付きフェッチ処理のためfetchを直接使用
            const result = await fetch(info, init)
            await resendableFetchStore.removeItem(key)
            setFetchingCount(prev => prev - 1)
            return result
        },
        []
    )

    const Fetch: Fetcher = useCallback(
        async (info: RequestInfo, init?: RequestInit) => {
            return await CachingFetch(info, init)
        },
        [CachingFetch]
    )

    useEffect(() => {
        if (firstLoad) {
            firstLoad = false

            resendableFetchStore
                .keys()
                .then((keys: string[]) => {
                    if (keys.length === 0) throw new Error("cache is empty") // キャッシュが空の場合は処理を中断
                    // TODO: Throttling and error handling
                    return Promise.allSettled(keys.map(key => Refetch(key)))
                })
                .catch((err: Error) => {
                    // cache is empty.（許容）
                })
            /*.then(result => {
                    // error handling
                    // {status: "fulfilled", value: any} or {status: "rejected", reason: string}
                })*/
        }
    }, [Refetch])

    return { Fetch, inProgress }
}

interface BackgroundFetchObject {
    Fetch: Fetcher
    inProgress: boolean
}

const BackgroundFetchContext = createContext<BackgroundFetchObject | undefined>(
    undefined
)

export const BackgroundFetchContextProvider = ({
    children
}: PropsWithChildren) => {
    const { Fetch, inProgress } = useBackgroundFetch()
    return (
        <BackgroundFetchContext.Provider value={{ Fetch, inProgress }}>
            {children}
        </BackgroundFetchContext.Provider>
    )
}

export const useBackgroundFetchCtx = (): BackgroundFetchObject | never => {
    const ctx = useContext(BackgroundFetchContext)
    if (!ctx) {
        throw new Error("BackgroundFetchContextProvider is not mounted")
    }
    return ctx
}
